/**
 * The anchor: a mark that says "a thread is about this text".
 *
 * A MARK, NOT AN OFFSET, and that is the entire design. A comment has to keep pointing at the same
 * words while people type around them, and a stored position does not survive a single edit three
 * paragraphs earlier. A mark moves with its text because ProseMirror maps it through every change.
 *
 * IT CARRIES NOTHING BUT AN ID. The conversation -- who asked, what they asked, who replied, when
 * it was resolved -- lives in the comment tables, because it has its own authors and times and it
 * outlives the sentence it was about. Putting the messages in the body would also mean a reviewer's
 * question travelled inside the procedure, which is the opposite of what a controlled document is.
 *
 * AND IT IS NOT SIGNED. The server strips comment marks before taking the content digest, so
 * placing one changes nothing about what an approval covers and nothing reads as an edit in the
 * history. That is what lets a document be commented on while it is OUT for approval -- which is
 * exactly when people want to.
 *
 * NOT INCLUSIVE: typing at the end of a commented phrase is new text, not more of what the comment
 * was about. An inclusive anchor grows every time somebody adds a word beside it, and a week later
 * the thread points at a paragraph nobody meant to discuss.
 */
import { Mark, mergeAttributes } from "@tiptap/core";

export type CommentOptions = {
    /** Which thread is being looked at, so it can be drawn differently from the rest. */
    get: () => { active: string | null; resolved: string[] };
};

export const CommentAnchor = Mark.create<CommentOptions>({
    name: "comment",
    inclusive: false,
    // SEVERAL THREADS CAN OVERLAP ONE PHRASE -- two people asking about the same sentence is the
    // normal case in a review, not an edge one -- so the marks coexist rather than replacing each
    // other.
    excludes: "",

    addOptions() {
        return { get: () => ({ active: null, resolved: [] }) };
    },

    addAttributes() {
        return {
            thread: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-comment"),
                renderHTML: (attributes) =>
                    attributes.thread ? { "data-comment": attributes.thread } : {},
            },
        };
    },

    parseHTML() {
        return [{ tag: "span[data-comment]" }];
    },

    renderHTML({ HTMLAttributes }) {
        const thread = HTMLAttributes["data-comment"];
        const { active, resolved } = this.options.get();
        // A RESOLVED THREAD KEEPS ITS ANCHOR AND LOSES ITS HIGHLIGHT. The mark stays in the body so
        // the thread can still be opened and still knows what it was about; it simply stops
        // colouring the procedure.
        const done = resolved.includes(thread);
        return ["span", mergeAttributes(HTMLAttributes, {
            class: "parkour-comment"
                + (done ? " parkour-comment-resolved" : "")
                + (active && active === thread ? " parkour-comment-active" : ""),
        }), 0];
    },
});
